// assets/AllCourses/Exercises/StressingSyllableWordsExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import { cn } from "@/utils/utils";

export default function StressingSyllableWordsExercise({
  exercise = {},
  onUpdate,           // Edit mode only
  errors = {},
  isPreview = false,  // false = admin edit, true = student view
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.stressingSyllableQuestions)
    ? exercise.stressingSyllableQuestions.map((q) => ({
        word: typeof q.word === "string" ? q.word.trim() : "",
        correctSyllable:
          typeof q.correctSyllable === "string"
            ? q.correctSyllable.trim()
            : "",
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {}, // { [qIndex]: string }
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;

    setState({
      answers:
        typeof exercise.attempt.answers === "object"
          ? exercise.attempt.answers
          : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  /* ================= RESET ================= */
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise?._id, isPreview]);

  useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "") =>
    String(text || "")
      .trim()
      .toLowerCase()
      .replace(/[.,!?]/g, "");

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("stressingSyllableQuestions", next);
  };

  const handleChange = (qIdx, field, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], [field]: value.trim() };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      { word: "", correctSyllable: "" },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT MODE ================= */
  const handleInputChange = (qIdx, value) => {
    if (isEditMode || showResults) return;

    setState((prev) => {
      const answers = {
        ...prev.answers,
        [qIdx]: value,
      };

      const next = {
        answers,
        hasInteracted: true,
      };

      setAttempt(next);

      const allAnswered = questions.every(
        (_, i) => answers[i]?.trim()
      );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, i) => {
          return (
            normalize(answers[i]) ===
            normalize(q.correctSyllable)
          );
        });

        onComplete(exercise._id, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE RENDER ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No words yet. Click “Add Word” to begin.
          </p>
        )}

        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <StringField
                  label={`Word ${qIdx + 1}`}
                  value={q.word}
                  onChange={(v) =>
                    handleChange(qIdx, "word", v)
                  }
                  placeholder="sandwich"
                  required
                  error={
                    errors?.[
                      `exercise-${exercise.index}-ss-word-${qIdx}`
                    ]
                  }
                />
              </div>

              {q.word && (
                <SpeakerButton text={q.word} size="lg" />
              )}
            </div>

            <StringField
              label="Stressed Syllable (Correct Answer)"
              value={q.correctSyllable}
              onChange={(v) =>
                handleChange(qIdx, "correctSyllable", v)
              }
              placeholder="sand"
              required
              error={
                errors?.[
                  `exercise-${exercise.index}-ss-correct-${qIdx}`
                ]
              }
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Word
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Word
        </Button>
      </div>
    );
  }

  /* ================= STUDENT VIEW ================= */
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No stressing syllable questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Stressed Syllable</CardTitle>
        <CardDescription>
          Listen to each word and type the stressed syllable
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const userAnswer = state.answers[qIdx] || "";
          const isCorrect =
            normalize(userAnswer) ===
            normalize(q.correctSyllable);

          return (
            <div
              key={qIdx}
              className="p-8 bg-muted rounded-xl space-y-6 text-center"
            >
              {/* Word & Audio */}
              <div className="flex justify-center items-center gap-6">
                <span className="text-4xl font-bold text-blue-700">
                  {q.word}
                </span>
                <SpeakerButton text={q.word} size="xl" />
              </div>

              {/* Input */}
              <div className="max-w-xs mx-auto">
                <Input
                  value={userAnswer}
                  onChange={(e) =>
                    handleInputChange(qIdx, e.target.value)
                  }
                  placeholder="Type stressed syllable"
                  disabled={showResults}
                  className={cn(
                  "text-2xl text-center font-bold h-16 transition border",

                  // correct
                  showResults &&
                    userAnswer &&
                    isCorrect &&
                    "bg-green-100 border-green-300",

                  // wrong
                  showResults &&
                    userAnswer &&
                    !isCorrect &&
                    "bg-red-100 border-red-300",

                  showResults && "cursor-not-allowed opacity-95"
                )}
                />
              </div>

              {/* Feedback */}
              {showResults && userAnswer && (
                  <div
                    className={cn(
                      "mt-4 p-4 rounded-lg border text-xl font-semibold",

                      isCorrect
                        ? "bg-green-100 border-green-300 text-green-700"
                        : "bg-red-100 border-red-300 text-red-700"
                    )}
                  >
                  {isCorrect
                    ? "Correct!"
                    : `Wrong — Correct: ${q.correctSyllable}`}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
